import { encodeQueryParams } from "@hex/common";
import { useCallback, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router";

import { TAO_PROJECT_VIEW, type TaoProjectView } from "./taoProjectViews";

/** Fresh project from the list; Code/Preview show the centered prompt until the first message. */
export const TAO_PROJECT_NEW_QUERY_PARAM = "new";

export type TaoProjectBranchQueryParams = {
  threadId?: string;
  view?: TaoProjectView;
  agentOpen?: boolean;
  newProject?: boolean;
};

interface TaoProjectViewState {
  selectedView: TaoProjectView;
  agentOpen: boolean;
  newProject: boolean;
}

export function useTaoProjectViewState(): TaoProjectViewState & {
  changeView: (view: TaoProjectView) => void;
  clearNewProject: (options?: { agentOpen?: boolean }) => void;
  openAgent: () => void;
  toggleAgent: () => void;
} {
  const location = useLocation();
  const navigate = useNavigate();
  const state = useMemo(
    () => parseTaoProjectViewState(new URLSearchParams(location.search)),
    [location.search],
  );

  const writeState = useCallback(
    (nextState: TaoProjectViewState) => {
      const params = new URLSearchParams(location.search);
      setTaoProjectViewParams(params, nextState);
      void navigate({ search: params.toString() }, { replace: true });
    },
    [location.search, navigate],
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const originalSearch = params.toString();
    setTaoProjectViewParams(params, state);
    if (params.toString() !== originalSearch) {
      writeState(state);
    }
  }, [location.search, state, writeState]);

  const changeView = useCallback(
    (selectedView: TaoProjectView) => {
      writeState({
        ...state,
        selectedView,
        agentOpen: selectedView === TAO_PROJECT_VIEW.chat || state.agentOpen,
      });
    },
    [state, writeState],
  );
  const clearNewProject = useCallback(
    (options?: { agentOpen?: boolean }) => {
      if (!state.newProject) return;
      writeState({
        ...state,
        agentOpen: options?.agentOpen ?? state.agentOpen,
        newProject: false,
      });
    },
    [state, writeState],
  );
  const openAgent = useCallback(() => {
    if (!state.agentOpen) {
      writeState({ ...state, agentOpen: true });
    }
  }, [state, writeState]);
  const toggleAgent = useCallback(() => {
    if (state.selectedView !== TAO_PROJECT_VIEW.chat) {
      writeState({ ...state, agentOpen: !state.agentOpen });
    }
  }, [state, writeState]);

  return { ...state, changeView, clearNewProject, openAgent, toggleAgent };
}

function parseView(value: string | null): TaoProjectView | null {
  switch (value) {
    case "notebook":
    case TAO_PROJECT_VIEW.code:
      return TAO_PROJECT_VIEW.code;
    case TAO_PROJECT_VIEW.chat:
    case TAO_PROJECT_VIEW.preview:
    case TAO_PROJECT_VIEW.split:
      return value;
    default:
      return null;
  }
}

function parseTaoProjectViewState(
  params: URLSearchParams,
): TaoProjectViewState {
  const legacyMainView = parseView(params.get("s1"));
  const legacySecondView = parseView(params.get("s2"));
  const selectedView = params.has("view")
    ? (parseView(params.get("view")) ?? TAO_PROJECT_VIEW.chat)
    : legacyMainView === TAO_PROJECT_VIEW.chat
      ? (legacySecondView ?? legacyMainView)
      : (legacyMainView ?? TAO_PROJECT_VIEW.chat);
  const legacyAgentOpen =
    legacyMainView == null ||
    legacyMainView === TAO_PROJECT_VIEW.chat ||
    legacySecondView === TAO_PROJECT_VIEW.chat;
  return {
    selectedView,
    agentOpen:
      selectedView === TAO_PROJECT_VIEW.chat ||
      (params.has("agent")
        ? params.get("agent") !== "0"
        : params.has("view") || legacyAgentOpen),
    newProject: params.get(TAO_PROJECT_NEW_QUERY_PARAM) === "1",
  };
}

function setTaoProjectViewParams(
  params: URLSearchParams,
  state: TaoProjectViewState,
): void {
  params.delete("s1");
  params.delete("s2");
  if (state.selectedView === TAO_PROJECT_VIEW.chat) {
    params.delete("view");
  } else {
    params.set("view", state.selectedView);
  }
  if (state.agentOpen || state.selectedView === TAO_PROJECT_VIEW.chat) {
    params.delete("agent");
  } else {
    params.set("agent", "0");
  }
  if (state.newProject) {
    params.set(TAO_PROJECT_NEW_QUERY_PARAM, "1");
  } else {
    params.delete(TAO_PROJECT_NEW_QUERY_PARAM);
  }
}

export function taoProjectBranchQueryString(
  queryParams?: TaoProjectBranchQueryParams,
): string {
  const params = new URLSearchParams();
  if (queryParams?.threadId != null) {
    params.set("threadId", queryParams.threadId);
  }
  setTaoProjectViewParams(params, {
    selectedView: queryParams?.view ?? TAO_PROJECT_VIEW.chat,
    agentOpen: queryParams?.agentOpen ?? true,
    newProject: queryParams?.newProject ?? false,
  });
  return encodeQueryParams(Object.fromEntries(params.entries()));
}
